import React, { useEffect, useState } from "react";
import { Loader2, AlertTriangle } from "lucide-react";
import { csuiteApi } from "../../../lib/csuiteApi";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

/** Enhanced SAP S/4HANA Executive Cockpit — Finance, Logistics, Procurement, HCM. */
export default function HanaCockpit() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { csuiteApi.hana().then(setD).catch(e => setErr(e.message)); }, []);
  if (err) return <div className="p-6 text-red-700 flex items-center gap-2"><AlertTriangle size={16}/>{err}</div>;
  if (!d) return <div className="p-10 grid place-items-center"><Loader2 className="animate-spin text-sky-700"/></div>;
  const m = d.modules;
  return (
    <div className="space-y-6" data-testid="platinum-hana">
      <div className="mb-2 flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">SAP S/4HANA</div>
          <h2 className="font-display text-2xl font-extrabold tracking-tight">Executive Cockpit</h2>
        </div>
        <span className={`pill text-[10px] ${d.source === "live" ? "pill-green" : "pill-neutral"}`} data-testid={`hana-source-${d.source || "mock"}`}>
          {d.source === "live" ? "Live · your S/4HANA" : "Demonstration data · connect your S/4HANA tenant on Connections to swap"}
        </span>
      </div>
      <div className="grid md:grid-cols-4 gap-3">
        <T label="Close · days"         value={m.finance.closeDays}         tone="blue" />
        <T label="Posting latency · ms" value={m.finance.postingLatencyMs}  tone="neutral" />
        <T label="Recon rate %"         value={`${m.finance.reconRate}%`}   tone="green" />
        <T label="Order cycle · hrs"    value={m.logistics.orderCycleH}     tone="neutral" />
        <T label="OTD rate %"           value={`${m.logistics.otdRate}%`}   tone="green" />
        <T label="Open backlog"         value={m.logistics.backlog}         tone="neutral" />
        <T label="Procurement cycle · d" value={m.procurement.cycleTimeD}   tone="neutral" />
        <T label="On-contract %"         value={`${m.procurement.onContractPct}%`} tone="blue" />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="card p-4">
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Module Adoption</div>
          <div style={{ width: "100%", height: 220 }}>
            <ResponsiveContainer><BarChart data={d.adoption} layout="vertical" margin={{ left: 40 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 10 }} />
              <YAxis type="category" dataKey="module" width={140} tick={{ fontSize: 10 }} />
              <Tooltip formatter={v => `${v}%`} contentStyle={{ fontSize: 12 }}/>
              <Bar dataKey="adoptedPct" fill="#0369A1" radius={[0,3,3,0]} />
            </BarChart></ResponsiveContainer>
          </div>
        </div>
        <div className="card p-4">
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Migration Waves</div>
          <ul className="text-sm">
            {d.migration.waves.map((w, i) => (
              <li key={i} className="py-2 border-b border-[var(--border)] last:border-0 flex items-center justify-between">
                <div>
                  <div className="font-medium">{w.wave}</div>
                  <div className="text-xs text-zinc-500 font-mono">{w.date}</div>
                </div>
                <span className={`pill text-[10px] ${w.status === "Live" ? "pill-green" : w.status === "In Cutover" ? "pill-amber" : w.status === "Testing" ? "pill-blue" : "pill-neutral"}`}>
                  {w.status}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

function T({ label, value, tone }) {
  const cls = tone === "green" ? "text-emerald-700" : tone === "blue" ? "text-sky-700" : "text-zinc-900";
  return (
    <div className="card p-4">
      <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`font-display text-2xl font-extrabold mt-1 ${cls}`}>{value}</div>
    </div>
  );
}
